import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  getCompanySubscription,
  updateSubscriptionPlan,
  SubscriptionInfo as SubscriptionInfoType,
} from "../../api/SubscriptionAPI";
import { getCompanyData } from "../../utils/generalUtil";

interface SubscriptionInfoProps {
  onPlanUpdated?: () => void;
}

const SubscriptionInfo: React.FC<SubscriptionInfoProps> = ({
  onPlanUpdated,
}) => {
  const [subscriptionInfo, setSubscriptionInfo] =
    useState<SubscriptionInfoType | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const fetchSubscriptionInfo = async () => {
    const companyData = getCompanyData();
    if (!companyData?.companyId) {
      setLoading(false);
      return;
    }

    const companyId = Number(companyData.companyId);
    const info = await getCompanySubscription(companyId);
    setSubscriptionInfo(info);
    setLoading(false);
  };

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  const handleUpdatePlan = async () => {
    if (!subscriptionInfo) return;

    setUpdating(true);
    const companyData = getCompanyData();
    if (companyData?.companyId) {
      const companyId = Number(companyData.companyId);
      const success = await updateSubscriptionPlan(
        companyId,
        subscriptionInfo.currentCourtCount
      );
      if (success) {
        await fetchSubscriptionInfo();
        onPlanUpdated?.();
      }
    }
    setUpdating(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!subscriptionInfo) {
    return (
      <Alert severity="error">
        Não foi possível carregar as informações da assinatura.
      </Alert>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const formatPrice = (price: number) => {
    return `R$ ${price.toFixed(2).replace(".", ",")}`;
  };

  const isTrialActive = () => {
    if (!subscriptionInfo.subscription.trialEndsAt) return false;
    const now = new Date();
    const trialEnd = new Date(subscriptionInfo.subscription.trialEndsAt);
    return now < trialEnd;
  };

  const getStatusText = () => {
    // Status: 0=trialing, 1=active, 2=past_due, 3=canceled
    if (isTrialActive()) {
      return "Período de Teste";
    }

    switch (subscriptionInfo.subscription.status) {
      case 1: // active
        return "Ativo";
      case 2: // past_due
        return "Em Atraso";
      case 3: // canceled
        return "Cancelado";
      default:
        return "Desconhecido";
    }
  };

  const getStatusColor = () => {
    if (isTrialActive()) return "warning";

    switch (subscriptionInfo.subscription.status) {
      case 1:
        return "success";
      case 2:
      case 3:
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Card sx={{ maxWidth: 600, mx: "auto", mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Informações da Assinatura
        </Typography>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Plano Atual
          </Typography>
          <Typography variant="h6" color="primary">
            {subscriptionInfo.subscription.plan.name}
          </Typography>
          <Typography variant="body2">
            {formatPrice(subscriptionInfo.subscription.plan.price)}/mês
          </Typography>
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Status
          </Typography>
          <Chip label={getStatusText()} color={getStatusColor()} size="small" />
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary">
            Quadras Cadastradas
          </Typography>
          <Typography variant="h6">
            {subscriptionInfo.currentCourtCount} quadra(s)
          </Typography>
        </Box>

        {isTrialActive() && subscriptionInfo.subscription.trialEndsAt && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Período de Teste
            </Typography>
            <Typography variant="body2">
              Até {formatDate(subscriptionInfo.subscription.trialEndsAt)}
            </Typography>
          </Box>
        )}

        {!subscriptionInfo.isPlanOptimal && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Recomendamos o plano{" "}
              <strong>{subscriptionInfo.recommendedPlanName}</strong> para{" "}
              {subscriptionInfo.currentCourtCount} quadra(s).
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleUpdatePlan}
              disabled={updating}
              sx={{ mt: 1 }}
            >
              {updating ? <CircularProgress size={20} /> : "Atualizar Plano"}
            </Button>
          </Alert>
        )}

        {subscriptionInfo.isPlanOptimal && (
          <Alert severity="success">
            Seu plano atual é ideal para o número de quadras cadastradas!
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionInfo;
